/**
 * Per-transferId control registry for ACTIVE sender upload loops
 * (`sendFile.ts` fresh sends + `senderResume.ts` resumes' shared
 * `uploadLoop.ts` invocation). This is the REAL-wiring counterpart of the
 * pure `SenderDeps.control`/`SenderResumeDeps.control` seam — a
 * module-level singleton, exactly like `offscreen/index.ts`'s
 * `activeReceives` map (receiver side) and `senderDeps.ext.ts`'s
 * `pendingAccepts` map (accept/decline seam): it bridges two independently
 * triggered events (the upload loop's per-part checkpoint, and an inbound
 * `file_ctrl` arriving via `notifyFileCtrlReceived`) that otherwise share no
 * state.
 *
 * Derived rules (docs/file-transfer-resume.md §8.2/§9.1 + this task's brief):
 *   - Pause NEVER sends a wire ctrl, regardless of trigger (local user
 *     action or inbound `pause` ctrl) — this registry never sends anything;
 *     it is pure state, consulted by the loop's checkpoint.
 *   - A LOCAL cancel sends `{kind:"cancel", reason:"user"}` (the loop's own
 *     catch site does this, using `getCancelOrigin`); a PEER-initiated
 *     cancel (inbound ctrl) does not echo one back — the peer already
 *     knows. `requestCancel` remembers WHICH origin asked.
 *   - Progress ctrls are receiver→sender only; the sender only ever
 *     DISPLAYS them, clamped monotonically (`clampMonotonicProgress`) so a
 *     stale/replayed ctrl can never regress the shown value.
 *
 * A transferId is only ever registered here while its backing record is
 * persisted `"transferring"` AND a loop is live in THIS panel context
 * (register happens right after P-S1/right after the server-wins reconcile —
 * see sendFile.ts/senderResume.ts). `hasActiveSend` therefore reflects the
 * LIVE-LOOP subset of §8.2's "Active record (transferring/paused)" column,
 * not the whole column: ctrls for a transferId with NO record (or a terminal
 * tombstone) find nothing here and are dropped per §8.2, but a `"paused"`
 * record's `cancel`/`error` ctrls are NOT dropped — they are
 * terminal-transitioned by the background SW
 * (`background/pausedTransferTerminal.ts`, §9.1's PAUSED rows), which is the
 * only context guaranteed alive when no loop is running.
 */

import type { FtErrorCode } from "@reflecto/core";

export type CancelOrigin = "local" | "peer";
export type InterruptSignal = "pause" | "cancel" | "peer-error";

interface ActiveSend {
  paused: boolean;
  cancelled: CancelOrigin | null;
  /** The `code` from an inbound terminal `error` ctrl, once one has arrived (§8.2 error row). */
  peerErrorCode: FtErrorCode | null;
  onPeerProgress?: (chunksDone: number) => void;
  lastPeerProgress?: number;
}

const activeSends = new Map<string, ActiveSend>();

/**
 * The pure-seam shape `SenderDeps.control`/`SenderResumeDeps.control`
 * declare (type-only import — no runtime dependency from the pure cores on
 * this module). `sendFile.ts`/`senderResume.ts` bind every call to a
 * specific `transferId` via a closure.
 */
export interface SendControlDeps {
  register(transferId: string, onPeerProgress?: (chunksDone: number) => void): void;
  deregister(transferId: string): void;
  checkInterrupt(transferId: string): InterruptSignal | undefined;
  getCancelOrigin(transferId: string): CancelOrigin | undefined;
  getPeerErrorCode(transferId: string): FtErrorCode | undefined;
}

/** Register `transferId` as having an active upload loop — call once the backing record is persisted `"transferring"` (P-S1, or the resume reconcile's server-wins save), before the loop starts. */
export function registerActiveSend(
  transferId: string,
  onPeerProgress?: (chunksDone: number) => void
): void {
  activeSends.set(transferId, {
    paused: false,
    cancelled: null,
    peerErrorCode: null,
    onPeerProgress,
  });
}

/** Deregister — call once the loop settles, on EVERY outcome (success, pause, cancel, or any other terminal failure). */
export function deregisterActiveSend(transferId: string): void {
  activeSends.delete(transferId);
}

/** Whether `transferId` currently has an active upload loop registered — the gate `notifyFileCtrlReceived` uses before acting on an inbound pause/cancel ctrl. */
export function hasActiveSend(transferId: string): boolean {
  return activeSends.has(transferId);
}

/** Request a pause for `transferId`'s active loop. No-op (§8.2 drop) if there is no active loop. */
export function requestPause(transferId: string): void {
  const s = activeSends.get(transferId);
  if (s) s.paused = true;
}

/**
 * Request a cancel for `transferId`'s active loop, remembering WHO asked
 * (§8.2 — only a LOCAL cancel echoes an outbound ctrl). First request wins;
 * a later duplicate (e.g. a replayed ctrl) is a no-op — idempotent per §8.2.
 * No-op (§8.2 drop) if there is no active loop.
 */
export function requestCancel(transferId: string, origin: CancelOrigin): void {
  const s = activeSends.get(transferId);
  if (s && !s.cancelled) s.cancelled = origin;
}

/**
 * Record that the PEER reported a terminal failure for `transferId`
 * (§8.2's `error` row, "Active record" column: "abort loop -> P-S4
 * `failed` with the ctrl's code + cleanup"). The receiver has already torn
 * its side down and deleted its partial output, so continuing to upload
 * only burns bandwidth and ends in a `done` the user must not be shown.
 *
 * First report wins, like `requestCancel` — a replayed `error` ctrl is a
 * no-op, and an already-cancelled loop keeps its cancel semantics (cancel
 * is checked first below). No-op (§8.2 drop) if there is no active loop;
 * a `"paused"` record's `error` is handled SW-side instead
 * (`background/pausedTransferTerminal.ts`, §9.1 PAUSED row).
 */
export function requestPeerError(transferId: string, code: FtErrorCode): void {
  const s = activeSends.get(transferId);
  if (s && !s.peerErrorCode) s.peerErrorCode = code;
}

/**
 * `SendControlDeps.checkInterrupt`'s real implementation — `uploadLoop.ts`'s
 * per-part checkpoint calls this (bound to a transferId) after every part.
 * A pending cancel takes priority over a concurrently-requested pause.
 */
export function checkInterrupt(transferId: string): InterruptSignal | undefined {
  const s = activeSends.get(transferId);
  if (!s) return undefined;
  if (s.cancelled) return "cancel";
  if (s.peerErrorCode) return "peer-error";
  if (s.paused) return "pause";
  return undefined;
}

/** `SendControlDeps.getCancelOrigin`'s real implementation — consulted only once `checkInterrupt` has returned `"cancel"`. */
export function getCancelOrigin(transferId: string): CancelOrigin | undefined {
  return activeSends.get(transferId)?.cancelled ?? undefined;
}

/** `SendControlDeps.getPeerErrorCode`'s real implementation — consulted only once `checkInterrupt` has returned `"peer-error"`, to carry the peer's code onto the sender's terminal record (§8.2). */
export function getPeerErrorCode(transferId: string): FtErrorCode | undefined {
  return activeSends.get(transferId)?.peerErrorCode ?? undefined;
}

/**
 * Monotonic clamp for inbound peer-progress ctrls (§8.2's "UI only: apply
 * max(shown, chunksDone)" row) — a stale/replayed progress ctrl must never
 * regress the displayed value. Pure, exported standalone for direct unit
 * testing independent of the registry's mutable state.
 */
export function clampMonotonicProgress(shown: number | undefined, incoming: number): number {
  return shown === undefined ? incoming : Math.max(shown, incoming);
}

/**
 * Route an inbound `progress` ctrl (the RECEIVER's download progress) to
 * the matching active send's UI callback, if any — applies the monotonic
 * clamp before invoking it. No-op if there is no active loop (or no
 * callback was registered) for this transferId.
 */
export function notifyPeerProgress(transferId: string, chunksDone: number): void {
  const s = activeSends.get(transferId);
  if (!s) return;
  s.lastPeerProgress = clampMonotonicProgress(s.lastPeerProgress, chunksDone);
  s.onPeerProgress?.(s.lastPeerProgress);
}

/** The bundled `SendControlDeps` implementation — wired into `SenderDeps.control`/`SenderResumeDeps.control` by senderDeps.ext.ts/senderResumeDeps.ext.ts. */
export const activeSendControl: SendControlDeps = {
  register: registerActiveSend,
  deregister: deregisterActiveSend,
  checkInterrupt,
  getCancelOrigin,
  getPeerErrorCode,
};

/** Test-only reset — mirrors other module-singleton test helpers in this codebase (e.g. background/index.ts's `__resetDismissStateForTests`). */
export function __resetActiveSendControlForTests(): void {
  activeSends.clear();
}
